import { useMemo } from 'react';
import MiniSearch, { SearchResult } from 'minisearch';
/**
 * React hook that search a list of items
 *
 * @param args - Search term, items and min length to search
 * @return Map of the search results or null if search is invalid
 */
export function useSearch({
	search,
	items,
	minLength = 2,
	minScore = 1,
}: Args): Map<string, SearchResult> | null {
	// Built during render rather than in an effect, so the results memo below
	// always searches the index for the current `items` -- an effect-populated
	// index lags one render behind, returning stale results when `items`
	// changes while `search` doesn't.
	const miniSearch = useMemo(() => {
		const index = new MiniSearch({
			fields: ['title', 'text'],
			storeFields: ['title'],
			searchOptions: {
				boost: { title: 2 },
				fuzzy: 0.4,
			},
		});
		index.addAll(items);
		return index;
	}, [items]);

	const response: Map<string, SearchResult> | null = useMemo(() => {
		if (search.length <= minLength) {
			return null;
		}

		return miniSearch.search(search, { fuzzy: 0.2 }).reduce((acc, result) => {
			const { id, score } = result;
			if (score >= minScore) {
				acc.set(id, result);
			}

			return acc;
		}, new Map());
	}, [search, miniSearch, minLength, minScore]);

	return response;
}

interface Args {
	search: string;
	items: Array<SearchItem>;
	minLength?: number;
	minScore?: number;
}

interface SearchItem {
	id: string;
	title: string;
	text: string;
}
